import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/auth";
import { KPIS, GROWTH, AUM_TREND } from "@/data/demo";
import { PageHeader, Card, Stat, Btn, Icon, DemoBadge, AIDisclaimer } from "@/components/kit";
import { BarChart, Bar, XAxis, ResponsiveContainer, Tooltip, Cell } from "recharts";

const INSIGHTS = [
  "Your AUM grew 7.2% this quarter, but 63% of growth came from three clients.",
  "Clients acquired through referrals have 18% higher average AUM.",
  "Your SIP book grew for the 3rd consecutive month — momentum is strong.",
];
const TABS = ["Analytics", "Brokerage", "Google Business", "Website"];

export default function Business() {
  const [tab, setTab] = useState("Analytics");
  const nav = useNavigate();
  const [story, setStory] = useState([]);
  useEffect(() => { api.get("/business/story").then((r) => setStory(r.data)).catch(() => {}); }, []);
  const [aum, setAum] = useState(50), [growth, setGrowth] = useState(75), [trail, setTrail] = useState(0.75);
  const annual = (v) => (v * 1e7 * (trail / 100));
  const fmt = (n) => n.toLocaleString("en-IN", { maximumFractionDigits: 0 });

  return (
    <div className="fade-up">
      <PageHeader title="MFD Business Intelligence" subtitle="Analytics, brokerage, Google reputation & your website — one control room." icon="BarChart3" color="#8B5CF6"
        action={<Btn onClick={() => nav("/app/ai", { state: { prompt: "Analyse my business performance this month: why might my net sales have fallen and what 3 actions should I take to grow AUM? Use my MFD context." } })} data-testid="business-ask-ai"><Icon name="Sparkles" size={16} /> Why did my sales change?</Btn>} />

      <div className="flex gap-2 mb-6 overflow-x-auto no-scrollbar">
        {TABS.map((t) => <button key={t} onClick={() => setTab(t)} data-testid={`business-tab-${t.replace(/\s/g, "-").toLowerCase()}`}
          className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap border ${tab === t ? "grad-blue-violet text-white border-transparent" : "bg-white border-gray-200 text-gray-600"}`}>{t}</button>)}
      </div>

      {tab === "Analytics" && (
        <div className="space-y-6">
          {story.length > 0 && (
            <div data-testid="business-story">
              <div className="flex items-center gap-2 mb-3">
                <Icon name="Sparkles" size={18} className="text-violet-400" />
                <h3 className="font-head font-semibold text-lg text-slate-100">Your business story</h3>
                <span className="text-xs text-slate-500">What changed · why · what to do</span>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                {story.map((s) => (
                  <div key={s.metric} className="card-mfx p-5" data-testid={`story-${s.metric.replace(/\s/g, "-").toLowerCase()}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div><div className="text-xs text-slate-400">{s.metric}</div><div className="font-head font-bold text-2xl" style={{ color: s.color }}>{s.value}</div></div>
                      <span className="text-sm font-semibold flex items-center gap-1" style={{ color: s.color }}><Icon name={s.trend === "up" ? "TrendingUp" : "TrendingDown"} size={16} />{s.delta >= 0 ? "+" : ""}{s.delta}%</span>
                    </div>
                    <StoryRow icon="Activity" tone="#94A3B8" label="What changed" text={s.changed} />
                    <StoryRow icon="HelpCircle" tone="#C084FC" label="Why" text={s.why} />
                    <StoryRow icon="Target" tone="#FBBF24" label="What to do" text={s.todo} />
                    <button onClick={() => nav(s.path)} data-testid={`story-cta-${s.metric.replace(/\s/g, "-").toLowerCase()}`}
                      className="mt-3 w-full grad-amber text-black font-semibold text-sm rounded-xl py-2.5 inline-flex items-center justify-center gap-1.5">{s.cta} <Icon name="ArrowRight" size={15} /></button>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Stat label="Gross Sales" value={KPIS.grossSales} color="#3B82F6" icon="ArrowUpRight" />
            <Stat label="Redemptions" value={KPIS.redemptions} color="#F97316" icon="ArrowDownRight" />
            <Stat label="Brokerage" value={KPIS.brokerage} delta={GROWTH.revenue} color="#8B5CF6" icon="Wallet" />
            <Stat label="Trail Income" value={KPIS.trail} color="#06B6D4" icon="Repeat" />
          </div>
          <Card>
            <div className="flex items-center justify-between mb-4"><h3 className="font-head font-semibold text-lg">AUM by month</h3><DemoBadge /></div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={AUM_TREND}><XAxis dataKey="m" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: "#9CA3AF" }} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #eee" }} cursor={{ fill: "#F9FAFB" }} />
                <Bar dataKey="aum" radius={[8, 8, 0, 0]}>{AUM_TREND.map((d, i) => <Cell key={d.m} fill={i === AUM_TREND.length - 1 ? "#8B5CF6" : "#C4B5FD"} />)}</Bar>
              </BarChart>
            </ResponsiveContainer>
          </Card>
          <Card>
            <h3 className="font-head font-semibold text-lg flex items-center gap-2 mb-3"><Icon name="Sparkles" size={18} className="text-violet-600" /> AI-generated insights</h3>
            <div className="space-y-2">{INSIGHTS.map((t) => <div key={t} className="flex gap-2 text-sm text-gray-700 bg-violet-50 rounded-xl px-4 py-3"><Icon name="Lightbulb" size={16} className="text-violet-500 shrink-0 mt-0.5" />{t}</div>)}</div>
            <AIDisclaimer />
          </Card>
        </div>
      )}

      {tab === "Brokerage" && (
        <Card className="max-w-2xl">
          <h3 className="font-head font-semibold text-lg mb-1">Brokerage Intelligence · Scenario modelling</h3>
          <p className="text-sm text-gray-500 mb-5">What happens to my revenue as AUM grows?</p>
          <Slider label={`Current AUM: ₹${aum} Cr`} val={aum} set={setAum} min={5} max={200} />
          <Slider label={`Target AUM: ₹${growth} Cr`} val={growth} set={setGrowth} min={5} max={300} />
          <Slider label={`Avg trail: ${trail}%`} val={trail} set={setTrail} min={0.1} max={1.5} step={0.05} />
          <div className="grid grid-cols-2 gap-4 mt-6">
            <div className="rounded-2xl bg-blue-50 p-5"><div className="text-xs text-gray-500">Annual trail @ current AUM</div><div className="font-head font-bold text-2xl text-blue-600 mt-1">₹{fmt(annual(aum))}</div></div>
            <div className="rounded-2xl bg-violet-50 p-5"><div className="text-xs text-gray-500">Annual trail @ target AUM</div><div className="font-head font-bold text-2xl text-violet-600 mt-1">₹{fmt(annual(growth))}</div></div>
          </div>
          <div className="mt-4 rounded-2xl grad-lime-cyan p-4 text-white text-sm font-medium">Growing from ₹{aum} Cr to ₹{growth} Cr adds approximately ₹{fmt(annual(growth) - annual(aum))} in annual trail income.</div>
          <div className="mt-3"><DemoBadge /></div>
        </Card>
      )}

      {tab === "Google Business" && (
        <div className="grid lg:grid-cols-3 gap-5">
          <div className="rounded-[24px] bg-[#0A0A0A] text-white p-7 text-center">
            <span className="text-sm text-gray-400">Google Reputation Score</span>
            <div className="font-head font-extrabold text-6xl mt-3 gradient-text">92</div>
            <div className="text-gray-400 text-sm">/ 100</div>
            <div className="mt-4 flex items-center justify-center gap-1 text-amber-400"><Icon name="Star" size={16} fill="#FBBF24" /> 4.7 · 68 reviews</div>
            <p className="text-xs text-gray-400 mt-3">Get 8 more reviews to reach 4.8 stars.</p>
          </div>
          <div className="lg:col-span-2 grid sm:grid-cols-2 gap-4">
            {[["Profile views", "3,420", "Eye", "#3B82F6"], ["Calls", "142", "Phone", "#8B5CF6"], ["Direction requests", "89", "Navigation", "#F97316"], ["Website visits", "506", "Globe", "#06B6D4"]].map(([l, v, i, c]) => (
              <Card key={l}><div className="flex items-center justify-between"><span className="text-xs text-gray-500">{l}</span><Icon name={i} size={16} style={{ color: c }} /></div><div className="font-head font-bold text-2xl mt-2" style={{ color: c }}>{v}</div></Card>
            ))}
            <Card className="sm:col-span-2"><div className="flex flex-wrap gap-2">
              <Btn data-testid="gbp-review-btn"><Icon name="Star" size={14} /> Request reviews</Btn>
              <Btn variant="soft"><Icon name="QrCode" size={14} /> Review QR code</Btn>
              <Btn variant="outline"><Icon name="Sparkles" size={14} /> AI reply to reviews</Btn>
            </div>
            <p className="text-[11px] text-gray-400 mt-3">Establish a legitimate professional presence in line with Google's policies and financial-advertising requirements.</p></Card>
          </div>
        </div>
      )}

      {tab === "Website" && (
        <Card className="max-w-2xl">
          <div className="flex items-center gap-3 mb-4"><div className="w-11 h-11 rounded-xl bg-blue-50 grid place-items-center"><Icon name="Globe" size={22} className="text-blue-600" /></div>
            <div><h3 className="font-head font-semibold text-lg">Your MFD microsite</h3><span className="text-sm text-blue-600">www.mfdx.in/arjun</span></div></div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-5">
            {["About", "Services", "MF Education", "Blog", "Videos", "Contact", "Reviews", "Book Meeting"].map((p) => (
              <div key={p} className="rounded-xl border border-gray-100 px-3 py-2 text-xs text-center text-gray-600">{p}</div>
            ))}
          </div>
          <div className="rounded-2xl grad-blue-violet p-6 text-white text-center">
            <Icon name="Sparkles" size={28} className="mx-auto mb-2" />
            <h4 className="font-head font-bold text-lg">Build My Website with AI</h4>
            <p className="text-white/85 text-sm mt-1">Auto-generate your entire microsite from your MFDX profile.</p>
            <button className="mt-4 bg-white text-blue-600 font-semibold rounded-full px-5 py-2.5 hover-lift" data-testid="website-build-btn">Generate site</button>
          </div>
        </Card>
      )}
    </div>
  );
}
function StoryRow({ icon, tone, label, text }) {
  return (
    <div className="flex gap-2 mb-2">
      <Icon name={icon} size={15} style={{ color: tone }} className="mt-0.5 shrink-0" />
      <div><span className="text-[11px] font-bold uppercase tracking-wide" style={{ color: tone }}>{label}</span><p className="text-sm text-slate-300 leading-snug">{text}</p></div>
    </div>
  );
}
function Slider({ label, val, set, min, max, step = 1 }) {
  return <div className="mb-5"><div className="flex justify-between text-sm font-medium mb-2"><span>{label}</span></div>
    <input type="range" min={min} max={max} step={step} value={val} onChange={(e) => set(Number(e.target.value))} className="w-full accent-violet-600" /></div>;
}
